<template>
  <v-dialog v-model="dialog" persistent max-width="600px" scrollable>
    <v-card class="editor-card">
      <v-card-title class="editor-header">
        <span class="editor-title">Edit Background</span>
        <v-btn icon @click="onClose" class="close-btn">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="editor-description app-regular-font">
        Upload a photo or choose an image that captures your interests or
        personality.
      </v-card-text>

      <v-divider class="my-2"></v-divider>

      <div class="editor-content">
        <!-- Upload Section -->
        <section class="editor-section">
          <h3 class="section-title">Upload a photo</h3>
          <div class="upload-container">
            <editable-background
              :image="profile.bgImage"
              :onImageSelected="onImageSelected"
              ref="editableBg"
              class="upload-preview"
            />
            <div class="upload-hint">Click to upload</div>
            <div class="upload-requirements">JPG or PNG, max 5MB</div>
          </div>
        </section>

        <v-divider class="my-4"></v-divider>

        <!-- Default Images Section -->
        <section class="editor-section">
          <h3 class="section-title">Choose from our collection</h3>
          <div class="default-images-grid">
            <div
              v-for="(img, index) in defaultImages"
              :key="img"
              class="image-tile"
              @click="chooseDefaultImage(index)"
              :class="{ selected: index === choosed }"
            >
              <v-img
                :src="img"
                aspect-ratio="1.5"
                class="image-tile-content"
                gradient="to top, rgba(0,0,0,0.4), rgba(0,0,0,0)"
              >
                <template v-slot:placeholder>
                  <v-row class="fill-height" justify="center" align="center">
                    <v-progress-circular
                      indeterminate
                      color="grey lighten-5"
                    ></v-progress-circular>
                  </v-row>
                </template>
              </v-img>
              <v-fade-transition>
                <div v-if="index === choosed" class="selection-badge">
                  <v-icon color="white">mdi-check-circle</v-icon>
                </div>
              </v-fade-transition>
            </div>
          </div>
        </section>
      </div>

      <!-- Error Message -->
      <v-slide-y-transition>
        <v-card-text v-if="message" class="error-message">
          <v-alert dense outlined type="error" class="mb-0">
            {{ message }}
          </v-alert>
        </v-card-text>
      </v-slide-y-transition>

      <!-- Actions -->
      <v-card-actions class="editor-actions">
        <v-btn
          depressed
          large
          color="primary"
          @click="onSave"
          class="action-btn"
          :loading="loading"
          :disabled="choosed === -1 && !selectedImage"
        >
          Save Changes
          <template v-slot:loader>
            <span class="custom-loader">
              <v-icon light>mdi-cached</v-icon>
            </span>
          </template>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";
import EditableBackground from "@/components/EditableBackground.vue";

export default {
  components: { EditableBackground },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    onClose: {
      type: Function
    }
  },
  data() {
    return {
      message: null,
      loading: false,
      selectedImage: null,
      defaultImages: Array.from(
        { length: 16 },
        (_, i) =>
          `https://weallvolunteer.s3.us-east-2.amazonaws.com/default_background/${i +
            1}.jpg`
      ),
      profile: {},
      choosed: -1
    };
  },
  methods: {
    ...mapActions("auth", {
      updateBackground: "updateBackground"
    }),
    onSave() {
      if (this.choosed === -1 && !this.selectedImage) {
        this.message = "Please upload or choose an image.";
        return;
      }

      const formData = new FormData();
      if (this.selectedImage) {
        formData.append("image", this.selectedImage);
      } else if (this.choosed !== -1) {
        formData.append("backgroundIndex", this.choosed);
      }

      this.loading = true;
      this.updateBackground(formData)
        .then(() => {
          this.loading = false;
          this.onClose();
        })
        .catch(error => {
          this.loading = false;
          this.message =
            error.response?.data?.message ||
            error.message ||
            "An error occurred";
        });
    },
    onImageSelected(image) {
      this.selectedImage = image;
      this.choosed = -1;
      this.message = null;
    },
    chooseDefaultImage(index) {
      this.choosed = index;
      this.selectedImage = null;
      this.message = null;
      this.$refs.editableBg?.clearSelected();
    }
  },
  computed: {
    ...mapState("auth", {
      myProfile: "profile"
    })
  },
  watch: {
    myProfile: {
      handler(newValue) {
        this.profile = { ...newValue };
      },
      immediate: true
    }
  }
};
</script>

<style scoped lang="scss">
.editor-card {
  border-radius: 12px !important;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);

  @media (max-width: 600px) {
    max-height: 100vh;
    border-radius: 0 !important;
    margin: 0;
  }
}

.editor-header {
  padding: 20px 24px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .editor-title {
    font-family: "Poppins-SemiBold", sans-serif;
    font-size: 1.25rem;
    color: #2c3e50;
  }

  .close-btn {
    color: #6c757d;
    transition: all 0.2s ease;

    &:hover {
      color: #495057;
      background-color: rgba(0, 0, 0, 0.05);
    }
  }
}

.editor-description {
  color: #6c757d;
  font-size: 0.9rem;
  padding-bottom: 8px;
}

.editor-content {
  // flex: 1;
  overflow-y: auto;
  padding: 0 24px;
}

.editor-section {
  margin: 16px 0;

  .section-title {
    font-family: "Poppins-Medium", sans-serif;
    color: #2c3e50;
    font-size: 1rem;
    margin-bottom: 12px;
  }
}

.upload-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 12px 0;

  .upload-preview {
    width: 100%;
    max-width: 400px;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s ease;

    &:hover {
      transform: translateY(-2px);
    }
  }

  .upload-hint {
    margin-top: 12px;
    color: #6c757d;
    font-size: 0.85rem;
  }

  .upload-requirements {
    color: #adb5bd;
    font-size: 0.75rem;
    margin-top: 4px;
  }
}

.default-images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
  margin-top: 12px;

  @media (min-width: 600px) {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  }
}

.image-tile {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
  }

  &.selected {
    box-shadow: 0 0 0 3px #0a5b8a;
  }
}

.image-tile-content {
  border-radius: 8px;
}

.selection-badge {
  position: absolute;
  top: 8px;
  right: 8px;
  background-color: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.error-message {
  padding-top: 0;
  padding-bottom: 8px;
}

.editor-actions {
  padding: 16px 24px;
  background-color: #f8f9fa;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: center;

  .action-btn {
    min-width: 200px;
    border-radius: 8px;
    text-transform: none;
    letter-spacing: normal;
    font-family: "Poppins-Medium", sans-serif;
    height: 44px;
    box-shadow: 0 2px 6px rgba(10, 91, 138, 0.2);
    transition: all 0.2s ease;

    &:hover {
      box-shadow: 0 4px 12px rgba(10, 91, 138, 0.3);
    }

    &:disabled {
      background-color: #e9ecef !important;
      color: #adb5bd !important;
      box-shadow: none;
    }
  }
}

.custom-loader {
  animation: loader 1s infinite;
  display: flex;
}

@keyframes loader {
  from {
    transform: rotate(0);
  }
  to {
    transform: rotate(360deg);
  }
}

.v-divider {
  border-color: rgba(0, 0, 0, 0.08) !important;
}
</style>
